import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cancelOssOrder,
  createOssChange,
  deleteOssOrder,
  deleteOssDocument,
  fetchOssChanges,
  fetchOssOrderDetail,
  getOssDocumentDownloadUrl,
  processOssAction,
  reviewOssChange,
  uploadOssDocument,
} from "../../api/ossApi";
import { displayLabel, orderStatus } from "../../constants/labels";
import { OssOrderFormDialog } from "../OssOrderFormDialog";
import { OssChangeRequestDialog } from "../changes/OssChangeRequestDialog";
import { OssDocumentUploadDialog } from "../documents/OssDocumentUploadDialog";
import { ActionDialog, ConfirmDialog } from "../orderDetail/OssOrderDetailDialogs";
import { OssOrderDetailTabs, OssOrderSummary } from "../orderDetail/OssOrderDetailSections";
import { isActionNeeded } from "../orderDetail/detailUtils";

export const OssOrderInlineDetail = ({
  open,
  groupCode,
  projectCode,
  orderId,
  onClose,
  onChanged,
}) => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changesLoading, setChangesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLine, setActionLine] = useState(null);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const load = useCallback(async () => {
    if (!open || !orderId) return;
    setLoading(true);
    setChangesLoading(true);
    setError("");
    setData(null);
    setChanges([]);
    try {
      const [detail, changeData] = await Promise.all([
        fetchOssOrderDetail(groupCode, projectCode, orderId),
        fetchOssChanges(groupCode, projectCode, { orderId, pageSize: 100 }),
      ]);
      setData(detail);
      setChanges(changeData?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || "주문 상세를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
      setChangesLoading(false);
    }
  }, [groupCode, open, orderId, projectCode]);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setData(null);
      setChanges([]);
      setError("");
      setEditOpen(false);
      setCancelOpen(false);
      setDeleteOpen(false);
      setActionLine(null);
      setDocumentOpen(false);
      setChangeOpen(false);
      setDocumentToDelete(null);
      return;
    }
    load();
  }, [load, open]);

  const order = data?.order || {};
  const lines = useMemo(() => data?.lines || [], [data?.lines]);
  const disabledByCancel = order.order_status === "CANCELLED";
  const summary = useMemo(() => {
    const openLines = lines.filter((line) => line.line_status !== "CANCELLED");
    const target = openLines.length ? openLines : lines;
    const minMargin = target
      .map((line) => Number(line.available_margin_days))
      .filter(Number.isFinite)
      .sort((a, b) => a - b)[0];

    return {
      lineCount: target.length,
      actionCount: target.filter(isActionNeeded).length,
      earliestStart: target.map((line) => line.required_start_date).filter(Boolean).sort()[0],
      earliestCheck: target.map((line) => line.check_required_date).filter(Boolean).sort()[0],
      minMargin: minMargin ?? "-",
    };
  }, [lines]);

  const dialogOrders = useMemo(
    () =>
      lines.map((line) => ({
        ...line,
        order_id: order.order_id,
        order_no: order.order_no,
        customer_name: order.customer_name,
      })),
    [lines, order.customer_name, order.order_id, order.order_no]
  );

  const reloadAfterChange = async () => {
    await load();
    onChanged?.();
  };

  const submitCancel = async () => {
    const targetOrderId = order.order_id || orderId;
    if (!targetOrderId) return;
    setSubmitting(true);
    setError("");
    try {
      await cancelOssOrder(groupCode, projectCode, targetOrderId, {
        reason: "주문 상세 모달에서 주문 취소",
        userKey: "order.manager",
      });
      setCancelOpen(false);
      setToast("주문을 취소했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "주문 취소 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    const targetOrderId = order.order_id || orderId;
    if (!targetOrderId) return;
    setSubmitting(true);
    setError("");
    try {
      await deleteOssOrder(groupCode, projectCode, targetOrderId);
      setDeleteOpen(false);
      setToast("주문을 삭제했습니다.");
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.message || "주문 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAction = async (payload) => {
    if (!actionLine) return;
    setSubmitting(true);
    setError("");
    try {
      await processOssAction(groupCode, projectCode, actionLine.order_line_id, {
        ...payload,
        userKey: "order.manager",
      });
      setActionLine(null);
      setToast(payload.nextCheckDate ? "다음 확인일을 예약했습니다." : "조치 처리를 완료했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "조치 처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDocument = async (file, payload) => {
    setSubmitting(true);
    setError("");
    try {
      await uploadOssDocument(groupCode, projectCode, file, payload);
      setDocumentOpen(false);
      setToast("문서를 업로드했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "문서 업로드 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitDocumentDelete = async () => {
    if (!documentToDelete) return;
    setSubmitting(true);
    setError("");
    try {
      await deleteOssDocument(groupCode, projectCode, documentToDelete.document_id);
      setDocumentToDelete(null);
      setToast("문서를 삭제했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "문서 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDocument = (document) => {
    window.open(getOssDocumentDownloadUrl(groupCode, projectCode, document.document_id), "_blank", "noopener,noreferrer");
  };

  const submitChange = async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      await createOssChange(groupCode, projectCode, payload);
      setChangeOpen(false);
      setToast("변경 요청을 등록했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "변경 요청 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitReview = async (row, status) => {
    setSubmitting(true);
    setError("");
    try {
      await reviewOssChange(groupCode, projectCode, row.change_request_id, {
        requestStatus: status,
        reviewedByUserKey: "order.manager",
        reviewNote: status === "APPROVED" ? "상세 모달에서 승인" : "상세 모달에서 반려",
      });
      setToast(status === "APPROVED" ? "변경 승인 및 일정 재계산을 완료했습니다." : "변경 요청을 반려했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "변경 검토 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            width: "min(1280px, calc(100vw - 36px))",
            height: "min(760px, calc(100dvh - 36px))",
            maxHeight: "calc(100dvh - 36px)",
            bgcolor: "#f3f6fa",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: "20px !important",
            py: "14px !important",
            pr: 6.2,
            borderBottom: "1px solid #e5e7eb",
            bgcolor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, pr: 22 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                display: "grid",
                placeItems: "center",
                borderRadius: "8px",
                bgcolor: "#eef2ff",
                color: "#6c5ce7",
                flexShrink: 0,
              }}
            >
              <Inventory2OutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 21, lineHeight: 1.2, fontWeight: "900 !important", color: "#111827" }}>
                  {order.order_no || "주문 상세"}
                </Typography>
                {order.order_status && (
                  <Box
                    sx={{
                      px: 0.85,
                      py: 0.25,
                      borderRadius: "999px",
                      bgcolor: "#eef2ff",
                      color: "#4f46e5",
                      fontSize: 11.5,
                      fontWeight: "800 !important",
                      flexShrink: 0,
                    }}
                  >
                    {displayLabel(orderStatus, order.order_status)}
                  </Box>
                )}
              </Stack>
              <Typography noWrap sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                {order.customer_name ? `${order.customer_name} · ${order.site_name || "납품처 미등록"}` : "주문 정보를 불러오는 중입니다."}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={0.6}
            alignItems="center"
            sx={{
              position: "absolute",
              right: 54,
              top: 14,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditRoundedIcon />}
              onClick={() => setEditOpen(true)}
              disabled={submitting || disabledByCancel || !data}
              sx={{ height: 32 }}
            >
              수정
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => setCancelOpen(true)}
              disabled={submitting || disabledByCancel || !data}
              sx={{ height: 32 }}
            >
              취소
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => setDeleteOpen(true)}
              disabled={submitting || !data}
              sx={{ height: 32 }}
            >
              삭제
            </Button>
          </Stack>
          <Button
            color="inherit"
            onClick={onClose}
            disabled={submitting}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              minWidth: 34,
              width: 34,
              height: 34,
              p: 0,
              color: "#6b7280",
            }}
          >
            <CloseRoundedIcon />
          </Button>
        </DialogTitle>

        <DialogContent
          sx={{
            p: "16px !important",
            bgcolor: "#eef2f7",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "grid", placeItems: "center", borderRadius: "8px", bgcolor: "#fff" }}>
              <CircularProgress size={32} />
            </Box>
          ) : error && !data ? (
            <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#fff" }}>
              <Alert severity="warning">{error}</Alert>
            </Box>
          ) : data ? (
            <Stack
              spacing={1.3}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: 8 },
                "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
                "&::-webkit-scrollbar-track": { backgroundColor: "#eef2f7" },
              }}
            >
              {error && <Alert severity="warning">{error}</Alert>}
              <OssOrderSummary order={order} summary={summary} compact />
              <OssOrderDetailTabs
                tab={tab}
                onTabChange={setTab}
                lines={lines}
                data={data}
                changes={changes}
                changesLoading={changesLoading || submitting}
                disabledByCancel={disabledByCancel}
                onAction={setActionLine}
                onDocumentUpload={() => setDocumentOpen(true)}
                onDocumentOpen={openDocument}
                onDocumentDelete={setDocumentToDelete}
                onChangeRequest={() => setChangeOpen(true)}
                onReviewChange={submitReview}
                compact
              />
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            px: "18px !important",
            py: "10px !important",
            minHeight: 52,
            alignItems: "center",
            justifyContent: "flex-end",
            borderTop: "1px solid #e5e7eb",
            bgcolor: "#fff",
          }}
        >
          <Button variant="contained" onClick={onClose} disabled={submitting} sx={{ minWidth: 84, height: 36 }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <OssOrderFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        groupCode={groupCode}
        projectCode={projectCode}
        orderId={order.order_id || orderId}
        initialData={data}
        onSaved={async () => {
          setEditOpen(false);
          setToast("주문 정보를 수정했습니다.");
          await reloadAfterChange();
        }}
      />

      <ConfirmDialog
        open={cancelOpen}
        title="주문 취소"
        message="주문과 품목 일정을 취소 상태로 전환합니다. 취소된 주문은 일정 화면에서 제외됩니다."
        confirmLabel="취소 처리"
        loading={submitting}
        onClose={() => setCancelOpen(false)}
        onConfirm={submitCancel}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="주문 삭제"
        message="주문과 연결된 품목, 문서, 변경 검토, 이력 데이터가 함께 삭제됩니다. 오등록 데이터 정리용으로만 사용해주세요."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={submitDelete}
      />

      <ActionDialog
        open={Boolean(actionLine)}
        line={actionLine}
        loading={submitting}
        onClose={() => setActionLine(null)}
        onSubmit={submitAction}
      />
      <OssDocumentUploadDialog
        open={documentOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setDocumentOpen(false)}
        onSubmit={submitDocument}
      />
      <OssChangeRequestDialog
        open={changeOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setChangeOpen(false)}
        onSubmit={submitChange}
      />
      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="문서 삭제"
        message="선택한 주문 문서를 삭제합니다."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={submitDocumentDelete}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
